import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { categoriesApi } from '../lib/api/categories';
import { inventoryApi } from '../lib/api/inventory';
import { MoonLoader } from '../components/ui/moon-loader';
import { Link } from 'react-router-dom';

type MenuItem = {
  id: string;
  name: string;
  description?: string;
  price: number;
  categoryId: string;
  category?: {
    id: string;
    name: string;
    icon?: string;
    color?: string;
  };
};

export default function RestaurantMenuPage() {
  const [menuType, setMenuType] = useState<'ALL' | 'FOOD' | 'DRINKS'>('ALL');
  
  // Use a hardcoded company ID for public menu access
  // This allows the menu to work without authentication
  const companyId = 'cmk2cy05s01r2n8daoq0rmmgs'; // Ikoro House company ID

  // Fetch all categories
  const { data: allCategories = [], isLoading: categoriesLoading, error: categoriesError } = useQuery({
    queryKey: ['public-categories', companyId],
    queryFn: () => categoriesApi.getCategories(companyId, true),  // true = skipAuth
  });

  // Fetch all products
  const { data: productsResponse, isLoading: productsLoading, error: productsError } = useQuery({
    queryKey: ['public-menu-products', companyId],
    queryFn: () => inventoryApi.getProducts({ 
      companyId: companyId,
      page: 1,
      pageSize: 1000,
      skipAuth: true  // Skip authentication for public menu access
    }),
  });

  const products = productsResponse?.items || [];
  const isLoading = categoriesLoading || productsLoading;

  // Logic to identify Food vs Drinks roots
  const { foodRootIds, drinkRootIds } = useMemo(() => {
    const foodKeywords = ['food', 'kitchen', 'restaurant', 'menu', 'grill', 'eats', 'buffet', 'breakfast', 'lunch', 'dinner'];
    const drinkKeywords = ['drink', 'beverage', 'bar', 'wine', 'beer', 'juice', 'cocktail', 'soda', 'water', 'spirit', 'coffee', 'tea'];
    
    const roots = allCategories.filter(c => !c.parentId);
    const fRoots = roots.filter(c => foodKeywords.some(kw => c.name.toLowerCase().includes(kw))).map(c => c.id);
    const dRoots = roots.filter(c => drinkKeywords.some(kw => c.name.toLowerCase().includes(kw))).map(c => c.id);

    return { foodRootIds: new Set(fRoots), drinkRootIds: new Set(dRoots) };
  }, [allCategories]);

  // Recursively determine if a category belongs to FOOD or DRINKS
  const getItemType = useMemo(() => (categoryId: string): 'FOOD' | 'DRINKS' => {
    const foodKeywords = ['food', 'kitchen', 'restaurant', 'menu', 'grill', 'eats', 'buffet', 'breakfast', 'lunch', 'dinner', 'snack', 'meal'];
    const drinkKeywords = ['drink', 'beverage', 'wine', 'beer', 'juice', 'water', 'cocktail', 'spirit', 'coffee', 'tea', 'bar', 'soda'];
    
    const checkAncestry = (id: string): 'FOOD' | 'DRINKS' | null => {
      if (foodRootIds.has(id)) return 'FOOD';
      if (drinkRootIds.has(id)) return 'DRINKS';
      
      const cat = allCategories.find(c => c.id === id);
      if (!cat) return null;
      
      // Check if this category name matches keywords
      const name = cat.name.toLowerCase();
      if (foodKeywords.some(kw => name.includes(kw))) return 'FOOD';
      if (drinkKeywords.some(kw => name.includes(kw))) return 'DRINKS';
      
      // Check parent recursively
      if (cat.parentId) return checkAncestry(cat.parentId);
      
      return null;
    };

    const type = checkAncestry(categoryId);
    if (type) return type;

    // Fallback: if no match found, default to FOOD
    return 'FOOD';
  }, [allCategories, foodRootIds, drinkRootIds]);

  // Build menu items from products with stock
  const menuItems = useMemo((): MenuItem[] => {
    const items: MenuItem[] = [];
    
    products.forEach((product: any) => {
      if (product.status !== 'ACTIVE' || !product.categoryId) return;
      
      // Check if product is a food item using hierarchical category check
      const isFoodItem = product.isFoodItem === true || 
                         (product as any).type === 'food' ||
                         product.trackInventory === false ||
                         getItemType(product.categoryId) === 'FOOD';
      
      // Check stock availability
      // EXCEPTION: Food items don't need stock (they're made-to-order)
      const availableQty = Number(product.availableQuantity) || 0;
      const stockQty = Number(product.stockQuantity) || 0;
      const hasStock = isFoodItem || availableQty > 0 || stockQty > 0;

      if (hasStock) {
        const category = allCategories.find(c => c.id === product.categoryId);
        items.push({
          id: product.id,
          name: product.name,
          description: product.description || product.shortDescription,
          price: Number(product.unitPrice) || 0,
          categoryId: product.categoryId,
          category: category ? {
            id: category.id,
            name: category.name,
            icon: category.icon,
            color: category.color
          } : undefined
        });
      }
    });

    return items;
  }, [products, allCategories, getItemType]);

  // Filter items by menu type
  const filteredItems = menuItems.filter(item => {
    if (menuType === 'ALL') return true;
    return menuType === getItemType(item.categoryId);
  });

  // Group items by category
  const groupedItems = useMemo(() => {
    const groups: Record<string, MenuItem[]> = {};
    filteredItems.forEach(item => {
      const catName = item.category?.name || 'Other';
      if (!groups[catName]) groups[catName] = [];
      groups[catName].push(item);
    });
    return groups;
  }, [filteredItems]);

  return (
    <div className="min-h-screen bg-[#fafafa] text-zinc-900 font-sans selection:bg-black selection:text-white pb-32">
      {/* Header with Filters */}
      <nav className="fixed top-0 w-full bg-white/80 backdrop-blur-md border-b border-zinc-100 z-50 no-print">
        <div className="max-w-4xl mx-auto px-6 h-14 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2 group">
            <img src="/brand-b.png" alt="Ikoro House" className="w-6 h-6 transition-transform group-hover:scale-105" />
            <span className="font-extrabold tracking-tight uppercase text-sm leading-none">Ikoro House</span>
          </Link>
          
          <div className="flex gap-8">
            {(['ALL', 'FOOD', 'DRINKS'] as const).map((type) => (
              <button
                key={type}
                onClick={() => setMenuType(type)}
                className={`text-[9px] font-bold tracking-[0.2em] transition-colors relative ${
                  menuType === type ? 'text-black' : 'text-zinc-400 hover:text-zinc-600'
                }`}
              >
                {type}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Mobile Filter Tabs - removed since filters are now in header */}

      <main className="max-w-3xl mx-auto px-6 pt-20 md:pt-28">
        {/* Title */}
        <div className="text-center mb-24 flex flex-col items-center">
          <div className="flex items-center gap-3 mb-6">
            <img src="/brand-b.png" alt="Ikoro House" className="w-[42px] h-[42px] opacity-90" />
            <h2 className="text-2xl md:text-[28px] font-black tracking-tight uppercase leading-none text-zinc-900 mt-1">Ikoro House</h2>
          </div>
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="h-px w-8 bg-zinc-200" />
            <p className="text-[10px] font-bold tracking-[0.4em] uppercase text-zinc-400">Kitchen & Bar</p>
            <div className="h-px w-8 bg-zinc-200" />
          </div>
          <h1 className="text-4xl md:text-5xl font-black uppercase italic tracking-tighter text-zinc-900 leading-none">
            The Menu
          </h1>
        </div>

        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-20 opacity-50">
            <MoonLoader size="sm" color="black" />
            <p className="mt-4 font-bold tracking-[0.3em] uppercase text-[9px] text-zinc-500 animate-pulse">Curating Menu</p>
          </div>
        ) : categoriesError || productsError ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="text-red-600 text-center">
              <p className="text-lg font-bold mb-2">Failed to load menu</p>
              {categoriesError && <p className="text-sm mb-1">Categories: {String(categoriesError)}</p>}
              {productsError && <p className="text-sm">Products: {String(productsError)}</p>}
              <p className="text-xs text-zinc-500 mt-4">Please check the console for more details</p>
            </div>
          </div>
        ) : (
          <div className="space-y-24">
            {Object.entries(groupedItems).map(([catName, items]) => (
              <section key={catName} className="relative">
                <div className="flex items-center gap-6 mb-10">
                  <h2 className="text-xs md:text-sm font-bold tracking-[0.3em] uppercase text-zinc-400 shrink-0">
                    {catName}
                  </h2>
                  <div className="h-[1px] w-full bg-gradient-to-r from-zinc-200 to-transparent" />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
                  {items.map((item) => (
                    <div key={item.id} className="group flex flex-col">
                      <div className="flex items-baseline gap-3 mb-2">
                        <h3 className="text-sm md:text-base font-bold text-zinc-900 group-hover:text-zinc-600 transition-colors whitespace-nowrap">
                          {item.name}
                        </h3>
                        <div className="flex-1 border-b border-dashed border-zinc-300" />
                        <span className="text-sm font-bold text-zinc-900 shrink-0">
                          {item.price > 0 ? `${item.price.toLocaleString()} RWF` : 'Not set'}
                        </span>
                      </div>
                      {item.description && (
                        <p className="text-[11px] md:text-xs text-zinc-500 leading-relaxed font-medium whitespace-pre-line">
                          {item.description.split(',').map((part, index, array) => (
                            <span key={index}>
                              {part.trim()}
                              {index < array.length - 1 && <br />}
                            </span>
                          ))}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </main>

      <footer className="max-w-3xl mx-auto px-6 py-16 text-center border-t border-zinc-200 mt-24">
        <p className="text-[9px] font-bold tracking-[0.4em] uppercase text-zinc-400 mb-2">
          Ikoro House Hospitality
        </p>
        <p className="text-[10px] text-zinc-500">Kigali, Rwanda &middot; ikoro-house</p>
      </footer>
    </div>
  );
}
